<template>
  <div class="d-flex flex-column ga-2">
    <div class="d-flex align-center justify-space-between">
      <div class="text-body-2 font-weight-semibold ncrm-text-strong">
        {{ $t("ncrm.workflows.nodeSettings.fileImport.historyTitle") }}
      </div>
      <v-btn
        size="x-small"
        variant="text"
        :loading="loading"
        :disabled="loading"
        @click="fetchHistory"
      >
        <v-icon start size="14">mdi-refresh</v-icon>
        {{ $t("app.actions.refresh") }}
      </v-btn>
    </div>

    <v-progress-linear v-if="loading && !batches.length" indeterminate color="primary" height="2" />

    <v-alert v-else-if="error" type="error" density="compact" class="text-body-2">
      {{ error }}
    </v-alert>

    <div v-else-if="!batches.length" class="text-caption text-medium-emphasis py-3 text-center">
      {{ $t("ncrm.workflows.nodeSettings.fileImport.historyEmpty") }}
    </div>

    <div v-else class="ncrm-file-import-history">
      <div class="ncrm-file-import-history__header">
        <span>{{ $t("ncrm.workflows.nodeSettings.fileImport.historyHeaderFile") }}</span>
        <span>{{ $t("ncrm.workflows.nodeSettings.fileImport.historyHeaderUploaded") }}</span>
        <span class="text-right">{{ $t("ncrm.workflows.nodeSettings.fileImport.historyHeaderRows") }}</span>
        <span>{{ $t("ncrm.workflows.nodeSettings.fileImport.historyHeaderStatus") }}</span>
      </div>
      <div v-for="batch in batches" :key="batch.id" class="ncrm-file-import-history__row">
        <span class="ncrm-file-import-history__name" :title="batch.original_file_name">
          <v-icon size="14" color="medium-emphasis">mdi-file-document-outline</v-icon>
          {{ batch.file_name }}
        </span>
        <span class="text-caption text-medium-emphasis">{{ formatDate(batch.uploaded_at) }}</span>
        <span class="text-caption text-right">{{ batch.row_count || 0 }}</span>
        <span>
          <v-chip size="x-small" :color="statusChip(batch.status).color" :variant="statusChip(batch.status).variant">
            {{ $t(`ncrm.workflows.nodeSettings.fileImport.historyStatus.${batch.status}`) }}
          </v-chip>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { listStagingFileImports } from "@/Utile/ncrmStagingFileImportApi.mjs";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

export default {
  name: "NcrmWorkflowFileImportHistory",
  props: {
    workflowInstanceId: { type: String, default: null },
  },
  data() {
    return { batches: [], loading: false, error: null };
  },
  watch: {
    workflowInstanceId: { immediate: true, handler() { this.fetchHistory(); } },
  },
  methods: {
    async fetchHistory() {
      if (!this.workflowInstanceId) { this.batches = []; return; }
      this.loading = true;
      this.error = null;
      try {
        const { data } = await listStagingFileImports(this.workflowInstanceId);
        this.batches = Array.isArray(data?.batches) ? data.batches : [];
      } catch (err) {
        this.error = err?.response?.data?.error || this.$t("ncrm.workflows.nodeSettings.fileImport.historyLoadError");
        this.batches = [];
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return "—";
      return formatDisplayDateTime(value);
    },
    statusChip(status) {
      return {
        uploaded: { color: "neutral", variant: "tonal" },
        parsed: { color: "info", variant: "tonal" },
        executed: { color: "success", variant: "tonal" },
        failed: { color: "error", variant: "tonal" },
      }[status] || { color: "neutral", variant: "tonal" };
    },
  },
};
</script>

<style scoped>
.ncrm-file-import-history { display: flex; flex-direction: column; gap: 4px; }
.ncrm-file-import-history__header {
  display: grid; grid-template-columns: 1fr 130px 70px 110px; gap: 10px; padding: 4px 8px;
  font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--app-text-default); opacity: 0.55; border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}
.ncrm-file-import-history__row {
  display: grid; grid-template-columns: 1fr 130px 70px 110px; gap: 10px; align-items: center; padding: 6px 8px;
  border-radius: 6px; font-size: 0.85rem; border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.04);
}
.ncrm-file-import-history__row:last-child { border-bottom: none; }
.ncrm-file-import-history__name {
  display: flex; align-items: center; gap: 6px; min-width: 0; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; font-weight: 500; color: var(--ncrm-color-text-strong);
}
</style>
